// src/components/ProtectedRoute.jsx
import { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuth0 } from '@auth0/auth0-react';

const orderedRoutes = [
  { path: '/', permission: 'read:dashboard' },
  { path: '/page1', permission: 'manage:orders' },
  { path: '/orderlist', permission: 'read:orders' },
  { path: '/page2', permission: 'manage:partners' },
  { path: '/brands', permission: 'manage:brands' },
  { path: '/locations', permission: 'manage:locations' },
  { path: '/add-partner', permission: 'create:partners' },
  { path: '/partner-details', permission: 'manage:partners' },
  { path: '/assets', permission: 'manage:assets' },
  { path: '/BillingManagement', permission: 'manage:billing' },
  { path: '/applicants', permission: 'manage:applicants' },
  { path: '/applicants/add', permission: 'create:applicants' },
  { path: '/applicant-details', permission: 'manage:applicant-details' },
  { path: '/user-management', permission: 'manage:users' },
  { path: '/settings', permission: 'manage:settings' },
];

const routePermissions = {
  '/': 'read:dashboard',
  '/page1': 'manage:orders',
  '/orderlist': 'read:orders',
  '/page2': 'manage:partners',
  '/brands': 'manage:brands',
  '/locations': 'manage:locations',
  '/add-partner': 'create:partners',
  '/partner-details': 'manage:partners',
  '/assets': 'manage:assets',
  '/BillingManagement': 'manage:billing',
  '/applicants': 'manage:applicants',
  '/applicants/add': 'create:applicants',
  '/applicant-details': 'manage:applicant-details',
  '/user-management': 'manage:users',
  '/settings': 'manage:settings',
};

export default function ProtectedRoute({ children }) {
  const { isAuthenticated, isLoading, loginWithRedirect, getAccessTokenSilently } = useAuth0();
  const [hasPermission, setHasPermission] = useState(false);
  const [isChecking, setIsChecking] = useState(true);
  const triggeredRef = useRef(false); // verhindert doppeltes Redirect
  const location = useLocation();
  const navigate = useNavigate();

  // 1) Nicht eingeloggt? -> direkt zu Google (kein /login)
  useEffect(() => {
    if (!isLoading && !isAuthenticated && !triggeredRef.current) {
      triggeredRef.current = true;
      loginWithRedirect({
        authorizationParams: {
          connection: 'google-oauth2',     // ggf. Enterprise-Name
          prompt: 'select_account',        // verhindert „silent re-login“ nach Logout
          // optional: prompt: 'login',     // erzwingt neue Anmeldung (SSO ignorieren)
        },
        appState: { returnTo: location.pathname + location.search },
      });
    }
  }, [isLoading, isAuthenticated, loginWithRedirect, location]);

  // 2) Eingeloggt? -> Permissions prüfen + ggf. umleiten
  useEffect(() => {
    const checkPermissions = async () => {
      try {
        if (!isAuthenticated) {
          setHasPermission(false);
          setIsChecking(false);
          return;
        }

        const token = await getAccessTokenSilently();
        const decoded = JSON.parse(atob(token.split('.')[1] || ''));
        const userPermissions = decoded?.permissions || [];

        // Home: hat der User read:dashboard? Falls nein -> erste erlaubte Route oder /unauthorized
        if (location.pathname === '/') {
          if (!userPermissions.includes('read:dashboard')) {
            const firstAvailable = orderedRoutes.find(r => userPermissions.includes(r.permission));
            if (firstAvailable) {
              navigate(firstAvailable.path, { replace: true });
              return;
            } else {
              navigate('/unauthorized', { replace: true });
              return;
            }
          }
        }

        // Erforderliche Permission für aktuelle Route ermitteln (exakt, sonst bestes Prefix)
        const path = location.pathname;
        let requiredPermission = routePermissions[path];

        if (!requiredPermission) {
          const basePath = Object.keys(routePermissions)
            .sort((a, b) => b.length - a.length)
            .find(route => route !== '/' && path.startsWith(route));
          requiredPermission = basePath ? routePermissions[basePath] : null;
        }

        if (!requiredPermission) {
          setHasPermission(true);
          setIsChecking(false);
          return;
        }

        const ok = userPermissions.includes(requiredPermission);
        setHasPermission(ok);
        setIsChecking(false);

        if (!ok) {
          navigate('/unauthorized', { replace: true });
        }
      } catch (err) {
        console.error('Permission check failed:', err);
        setHasPermission(false);
        setIsChecking(false);
        navigate('/unauthorized', { replace: true });
      }
    };

    if (isAuthenticated) checkPermissions();
  }, [isAuthenticated, getAccessTokenSilently, location, navigate]);

  // Nichts anzeigen, solange Auth0 lädt, wir redirecten oder prüfen
  if (isLoading || isChecking || !isAuthenticated) return null;

  if (!hasPermission) return null; // wir haben bereits nach /unauthorized navigiert

  return children;
}
